import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '../supabase';
import { useWhitelistRole } from '../useWhitelistRole';
import { FERIADOS_PERU } from '../utils/workingDays';
import useBreakpoint from '../hooks/useBreakpoint';
import { sheet, pageMobile, COMPACT_TOPBAR_OFFSET, dialog } from '../mobileStyles';
import BottomSheet from './BottomSheet';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const DOW_LABELS = ['LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES'];
// Compact agenda day headers, e.g. "Lun 6 oct" (index = Date#getDay / #getMonth).
const DOW_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTH_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Deterministic project palette — assigned by project index in the list
// (ordered by created_at) so colors stay stable across sessions.
const PROJECT_PALETTE = [
  '#A8453D', '#3F5D45', '#C87941', '#6E8CAE', '#C9A227',
  '#453674', '#4C8368', '#4B3E7D', '#7A4A2A', '#2A5A6B',
];

// Fallback palette for team members without equipo.color (same system as Journal).
const MEMBER_FALLBACK_PALETTE = [
  '#6B1C2A', '#C97B32', '#2A5C3E', '#1C3E6B', '#6B2A5C', '#3E6B1C',
];

const TITULO_SUGGESTIONS = [
  'REUNIÓN 01', 'REUNIÓN 02', 'EXPEDIENTE TÉCNICO', 'VISITA TÉCNICA', 'DETALLE MUEBLE',
];

const WINE = '#6B2D3E';
const MAUVE = '#C9A2AB';
const HOLIDAY_BG = '#DADADA';
const AUSENCIA_BG = '#E4CCCF';

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// Normalize whatever Supabase returns for a date column to strict YYYY-MM-DD.
function fechaKey(f) {
  if (!f) return '';
  return String(f).slice(0, 10);
}
function norm(s) {
  return (s == null ? '' : String(s)).trim().toLowerCase();
}
function initialsOf(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function hashIndex(str, mod) {
  if (!str) return 0;
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % mod;
}
// Short uppercase code for a project, e.g. "Casa Miraflores" → "CM".
function projectCode(nombre) {
  if (!nombre) return '??';
  const parts = nombre.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
  return parts.slice(0, 3).map(p => p[0]).join('').toUpperCase();
}

// Build the weeks of a month as rows of 5 cells (Mon–Fri only, no weekends).
// Cells outside the month are null; all-null rows (month starting on a
// weekend) are dropped.
function buildWeeks(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const start = new Date(first);
  start.setDate(first.getDate() - ((first.getDay() + 6) % 7)); // back to Monday
  const weeks = [];
  const cur = new Date(start);
  while (cur <= last) {
    const week = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(cur);
      d.setDate(cur.getDate() + i);
      week.push(d.getMonth() === month ? d : null);
    }
    weeks.push(week);
    cur.setDate(cur.getDate() + 7);
  }
  return weeks.filter(w => w.some(Boolean));
}

function CalendarNavIcon({ dir }) {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {dir === 'left' ? <path d="M12 4l-6 6 6 6" /> : <path d="M8 4l6 6-6 6" />}
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6h12" />
      <path d="M8 6V4a1 1 0 011-1h2a1 1 0 011 1v2" />
      <path d="M6 6l1 10a2 2 0 002 2h2a2 2 0 002-2l1-10" />
    </svg>
  );
}

function EditIcon() {
  return <span aria-hidden="true" style={{ fontSize: 11, lineHeight: 1 }}>✏️</span>;
}

export default function CronogramaProyectos() {
  const { isAdmin } = useWhitelistRole();
  const { isMobile, isCompact } = useBreakpoint();
  // Compact (< 1024px) only: tapped agenda item shown in a BottomSheet, and
  // the collapsible legend (collapsed by default).
  const [agendaSheet, setAgendaSheet] = useState(null); // { type: 'evento'|'ausencia', id }
  const [legendOpen, setLegendOpen] = useState(false);
  const today = new Date();
  const todayKey = ymd(today); // highlighted cell in whichever month it falls
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [events, setEvents] = useState([]);
  const [ausencias, setAusencias] = useState([]);
  const [feriados, setFeriados] = useState([]); // custom holidays (cronograma_feriados)
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Hover popover on an existing event / ausencia:
  // { type: 'evento'|'ausencia', id, x, y } — x/y are viewport coords
  // (position: fixed) so the popover is never clipped by parent containers.
  const [popover, setPopover] = useState(null);
  const hoverTimerRef = useRef(null); // 600ms open delay
  const closeTimerRef = useRef(null); // short grace period to reach the popover

  const openPopoverSoon = useCallback((type, id, rect) => {
    clearTimeout(hoverTimerRef.current);
    clearTimeout(closeTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setPopover({ type, id, x: rect.left, y: rect.bottom + 4 });
    }, 600);
  }, []);
  const scheduleClosePopover = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setPopover(null), 180);
  }, []);
  const cancelClosePopover = useCallback(() => {
    clearTimeout(closeTimerRef.current);
  }, []);
  useEffect(() => () => {
    clearTimeout(hoverTimerRef.current);
    clearTimeout(closeTimerRef.current);
  }, []);

  // Modals — `id` present means edit mode, absent/null means create.
  const [eventModal, setEventModal] = useState(null);   // { id?, fecha, proyecto_id, responsables: [], titulo }
  const [ausenciaModal, setAusenciaModal] = useState(null); // { id?, responsable_nombre, fecha_inicio, fecha_fin, etiqueta }
  const [feriadoModal, setFeriadoModal] = useState(null); // { fecha, nombre }
  // Delete confirmation: { type: 'evento'|'ausencia', id }
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Visible range: the cursor month.
  const rangeStart = useMemo(() => ymd(new Date(cursor.getFullYear(), cursor.getMonth(), 1)), [cursor]);
  const rangeEnd = useMemo(() => ymd(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0)), [cursor]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [evRes, auRes, prRes, eqRes, feRes] = await Promise.all([
      supabase.from('cronograma_eventos')
        .select('*')
        .gte('fecha', rangeStart)
        .lte('fecha', rangeEnd)
        .order('created_at', { ascending: true }),
      supabase.from('cronograma_ausencias')
        .select('*')
        .lte('fecha_inicio', rangeEnd)
        .gte('fecha_fin', rangeStart)
        .order('created_at', { ascending: true }),
      supabase.from('proyectos')
        .select('id, nombre')
        .order('created_at', { ascending: true }),
      supabase.from('equipo')
        .select('id, nombre, email, color, estado')
        .order('nombre', { ascending: true }),
      supabase.from('cronograma_feriados')
        .select('*')
        .order('fecha', { ascending: true }),
    ]);
    if (!evRes.error) setEvents(evRes.data || []);
    if (!auRes.error) setAusencias(auRes.data || []);
    if (!prRes.error) setProjects(prRes.data || []);
    if (!eqRes.error) setTeam(eqRes.data || []);
    if (!feRes.error) setFeriados(feRes.data || []);
    setLoading(false);
  }, [rangeStart, rangeEnd]);

  useEffect(() => { loadData(); }, [loadData]);

  // Close popover on Escape.
  useEffect(() => {
    if (!popover) return;
    function onKey(e) { if (e.key === 'Escape') setPopover(null); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [popover]);

  // Eventos and ausencias are a shared studio calendar: every signed-in user
  // can create, edit and delete any of them, and pick any responsable. Only
  // the feriados stay admin-only (see isAdmin below).

  // Deterministic color per project: index in the created_at-ordered list.
  const projectColorMap = useMemo(() => {
    const map = {};
    projects.forEach((p, i) => { map[p.id] = PROJECT_PALETTE[i % PROJECT_PALETTE.length]; });
    return map;
  }, [projects]);
  const projectById = useMemo(() => {
    const map = {};
    projects.forEach(p => { map[p.id] = p; });
    return map;
  }, [projects]);

  const memberColor = useCallback((name) => {
    const m = team.find(t => norm(t.nombre) === norm(name));
    if (m?.color) return m.color;
    return MEMBER_FALLBACK_PALETTE[hashIndex(norm(name), MEMBER_FALLBACK_PALETTE.length)];
  }, [team]);

  // Custom holidays indexed by date, and the merged holiday set: a day is a
  // holiday if it's a Peru national holiday OR a custom cronograma_feriados row.
  const customFeriadoByDate = useMemo(() => {
    const map = {};
    for (const f of feriados) map[fechaKey(f.fecha)] = f;
    return map;
  }, [feriados]);

  const holidaySet = useMemo(() => {
    const s = new Set(FERIADOS_PERU);
    for (const f of feriados) s.add(fechaKey(f.fecha));
    return s;
  }, [feriados]);

  const eventsByDate = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const key = fechaKey(ev.fecha);
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    }
    return map;
  }, [events]);

  function ausenciasForDay(key) {
    if (holidaySet.has(key)) return []; // holidays split the banner
    return ausencias.filter(a => fechaKey(a.fecha_inicio) <= key && key <= fechaKey(a.fecha_fin));
  }

  // Legend: only projects that have events in the visible range.
  const visibleProjects = useMemo(() => {
    const ids = new Set(events.map(e => e.proyecto_id).filter(Boolean));
    return projects.filter(p => ids.has(p.id));
  }, [events, projects]);

  // Peru national holidays in the visible months (legend).
  const visibleFeriados = useMemo(() => {
    return [...FERIADOS_PERU]
      .filter(f => f >= rangeStart && f <= rangeEnd)
      .sort();
  }, [rangeStart, rangeEnd]);

  // Custom holidays in the visible months (legend, shown separately).
  const visibleCustomFeriados = useMemo(() => {
    return feriados.filter(f => {
      const key = fechaKey(f.fecha);
      return key >= rangeStart && key <= rangeEnd;
    });
  }, [feriados, rangeStart, rangeEnd]);

  const monthA = cursor;

  function shiftMonth(delta) {
    setPopover(null);
    setCursor(c => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  }

  function openEventModal(dateKey) {
    setPopover(null);
    setEventModal({
      id: null,
      fecha: dateKey,
      proyecto_id: '',
      responsables: [],
      titulo: '',
    });
  }

  function openEditEventModal(ev) {
    setPopover(null);
    setEventModal({
      id: ev.id,
      fecha: fechaKey(ev.fecha),
      proyecto_id: ev.proyecto_id || '',
      responsables: [...(ev.responsables || [])],
      titulo: ev.titulo || '',
    });
  }

  function openEditAusenciaModal(a) {
    setPopover(null);
    setAusenciaModal({
      id: a.id,
      responsable_nombre: a.responsable_nombre || '',
      fecha_inicio: fechaKey(a.fecha_inicio),
      fecha_fin: fechaKey(a.fecha_fin),
      etiqueta: a.etiqueta || 'AUSENCIA',
    });
  }

  async function saveEvent() {
    if (!eventModal.fecha || !eventModal.proyecto_id || !eventModal.titulo.trim()) {
      alert('Completa fecha, proyecto y título.');
      return;
    }
    const payload = {
      fecha: eventModal.fecha,
      proyecto_id: eventModal.proyecto_id,
      responsables: eventModal.responsables,
      titulo: eventModal.titulo.trim().toUpperCase(),
    };
    setSaving(true);
    const { error } = eventModal.id
      ? await supabase.from('cronograma_eventos').update(payload).eq('id', eventModal.id)
      : await supabase.from('cronograma_eventos').insert([payload]);
    setSaving(false);
    if (error) {
      alert((eventModal.id ? 'Error actualizando evento: ' : 'Error guardando evento: ') + error.message);
      return;
    }
    setEventModal(null);
    // Always reload: the date may have moved the event to another day — or out
    // of the visible range entirely — so patching local state isn't enough.
    loadData();
  }

  async function saveAusencia() {
    if (!ausenciaModal.responsable_nombre || !ausenciaModal.fecha_inicio || !ausenciaModal.fecha_fin) {
      alert('Completa responsable y fechas.');
      return;
    }
    if (ausenciaModal.fecha_fin < ausenciaModal.fecha_inicio) {
      alert('La fecha fin no puede ser anterior a la fecha inicio.');
      return;
    }
    const payload = {
      responsable_nombre: ausenciaModal.responsable_nombre,
      fecha_inicio: ausenciaModal.fecha_inicio,
      fecha_fin: ausenciaModal.fecha_fin,
      etiqueta: (ausenciaModal.etiqueta || 'AUSENCIA').trim().toUpperCase(),
    };
    setSaving(true);
    if (ausenciaModal.id) {
      const { error } = await supabase.from('cronograma_ausencias')
        .update(payload)
        .eq('id', ausenciaModal.id);
      setSaving(false);
      if (error) { alert('Error actualizando ausencia: ' + error.message); return; }
      setAusencias(prev => prev.map(a => (a.id === ausenciaModal.id ? { ...a, ...payload } : a)));
    } else {
      const { error } = await supabase.from('cronograma_ausencias').insert([payload]);
      setSaving(false);
      if (error) { alert('Error guardando ausencia: ' + error.message); return; }
      loadData();
    }
    setAusenciaModal(null);
  }

  async function runConfirmedDelete() {
    if (!confirmDelete) return;
    const { type, id } = confirmDelete;
    if (type === 'evento') {
      const { error } = await supabase.from('cronograma_eventos').delete().eq('id', id);
      if (error) { alert('Error: ' + error.message); return; }
      setEvents(prev => prev.filter(e => e.id !== id));
    } else {
      const { error } = await supabase.from('cronograma_ausencias').delete().eq('id', id);
      if (error) { alert('Error: ' + error.message); return; }
      setAusencias(prev => prev.filter(x => x.id !== id));
    }
    setConfirmDelete(null);
    setPopover(null);
  }

  async function saveFeriado() {
    if (!feriadoModal.fecha) {
      alert('Selecciona una fecha.');
      return;
    }
    if (holidaySet.has(feriadoModal.fecha)) {
      alert('Esa fecha ya es feriado.');
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('cronograma_feriados').insert([{
      fecha: feriadoModal.fecha,
      nombre: feriadoModal.nombre.trim() || 'Feriado',
    }]);
    setSaving(false);
    if (error) { alert('Error guardando feriado: ' + error.message); return; }
    setFeriadoModal(null);
    loadData();
  }

  async function deleteFeriado(id) {
    if (!isAdmin) return;
    if (!window.confirm('¿Eliminar este feriado?')) return;
    const { error } = await supabase.from('cronograma_feriados').delete().eq('id', id);
    if (error) { alert('Error: ' + error.message); return; }
    loadData();
  }

  function fmtFeriado(key) {
    const d = new Date(key + 'T00:00:00');
    return d.toLocaleDateString('es-PE', { day: 'numeric', month: 'long' });
  }

  function renderMonth(monthDate) {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const weeks = buildWeeks(year, month);
    return (
      <div style={styles.monthCard}>
        <div style={styles.monthHeader}>{MONTH_NAMES[month]} {year}</div>
        <div style={styles.dowRow}>
          {DOW_LABELS.map(l => <div key={l} style={styles.dowCell}>{l}</div>)}
        </div>
        {weeks.map((week, wi) => (
          <div key={wi} style={styles.weekRow}>
            {week.map((d, di) => {
              if (!d) return <div key={di} style={styles.emptyCell} />;
              const key = ymd(d);
              const isHoliday = holidaySet.has(key);
              const isToday = key === todayKey;
              const dayEvents = eventsByDate[key] || [];
              const dayAusencias = ausenciasForDay(key);
              const dayNumStyle = isToday ? { ...styles.dayNum, ...styles.dayNumToday } : styles.dayNum;
              if (isHoliday) {
                return (
                  <div key={di} style={isToday ? { ...styles.holidayCell, ...styles.todayRing } : styles.holidayCell}>
                    <div style={dayNumStyle}>{d.getDate()}</div>
                    <div style={styles.holidayLabel}>
                      {customFeriadoByDate[key]?.nombre || 'Feriado'}
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={di}
                  style={{
                    ...styles.dayCell,
                    ...(isToday ? styles.todayCell : {}),
                    cursor: 'pointer',
                  }}
                  onClick={() => openEventModal(key)}
                  title={isToday ? 'Hoy — agregar evento' : 'Agregar evento'}
                >
                  <div style={dayNumStyle}>{d.getDate()}</div>
                  <div style={styles.eventStack}>
                    {dayEvents.map(ev => {
                      const color = projectColorMap[ev.proyecto_id] || '#8B8B8B';
                      const proj = projectById[ev.proyecto_id];
                      const code = projectCode(proj?.nombre);
                      return (
                        <div key={ev.id} style={styles.eventPillWrap}>
                          <div
                            style={styles.eventPill}
                            onClick={e => e.stopPropagation()}
                            onMouseEnter={e => openPopoverSoon('evento', ev.id, e.currentTarget.getBoundingClientRect())}
                            onMouseLeave={scheduleClosePopover}
                          >
                            {(ev.responsables || []).map(r => (
                              <span key={r} style={{ ...styles.respBadge, background: memberColor(r) }} title={r}>
                                {initialsOf(r)}
                              </span>
                            ))}
                            <span style={{ ...styles.eventBar, background: color }}>
                              {code}: {ev.titulo}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {dayAusencias.map(a => (
                    <div key={a.id} style={styles.eventPillWrap}>
                      <div
                        style={styles.ausenciaBanner}
                        onClick={e => e.stopPropagation()}
                        onMouseEnter={e => openPopoverSoon('ausencia', a.id, e.currentTarget.getBoundingClientRect())}
                        onMouseLeave={scheduleClosePopover}
                      >
                        {a.etiqueta || 'AUSENCIA'}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  // Compact (< 1024px) replacement for the month grid: a vertical agenda that
  // lists only the days with something on them. Weekdays mirror the grid
  // (events, holiday, ausencias); weekend days appear only if they have events.
  function renderAgenda() {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let n = 1; n <= lastDay; n++) {
      const d = new Date(year, month, n);
      const key = ymd(d);
      const dow = d.getDay();
      const isWeekday = dow >= 1 && dow <= 5;
      const dayEvents = eventsByDate[key] || [];
      const isHoliday = isWeekday && holidaySet.has(key);
      const dayAusencias = isWeekday ? ausenciasForDay(key) : [];
      if (!dayEvents.length && !isHoliday && !dayAusencias.length) continue;
      days.push({ d, key, isWeekday, isHoliday, dayEvents, dayAusencias });
    }
    if (days.length === 0) {
      return <div style={styles.agendaEmpty}>Sin eventos ni ausencias en este mes</div>;
    }
    return (
      <div style={styles.agenda}>
        {days.map(({ d, key, isWeekday, isHoliday, dayEvents, dayAusencias }) => {
          const isToday = key === todayKey;
          return (
            <section key={key} style={styles.agendaDay}>
              <div style={styles.agendaDayHeader}>
                <span style={isToday ? { ...styles.agendaDayLabel, color: WINE } : styles.agendaDayLabel}>
                  {DOW_SHORT[d.getDay()]} {d.getDate()} {MONTH_SHORT[d.getMonth()]}
                  {isToday && <span style={styles.agendaTodayTag}>Hoy</span>}
                </span>
                {isWeekday && !isHoliday && (
                  <button
                    type="button"
                    style={styles.agendaAddBtn}
                    onClick={() => openEventModal(key)}
                    aria-label={`Agregar evento el ${d.getDate()}`}
                  >
                    +
                  </button>
                )}
              </div>
              {isHoliday && (
                <div style={{ ...styles.agendaRow, ...styles.agendaRowStatic, background: HOLIDAY_BG }}>
                  <span style={styles.agendaRowBody}>
                    <span style={{ ...styles.agendaRowTitle, color: '#4A4A4A' }}>
                      {customFeriadoByDate[key]?.nombre || 'Feriado'}
                    </span>
                    <span style={styles.agendaRowMeta}>Feriado</span>
                  </span>
                </div>
              )}
              {dayEvents.map(ev => {
                const color = projectColorMap[ev.proyecto_id] || '#8B8B8B';
                const proj = projectById[ev.proyecto_id];
                const code = projectCode(proj?.nombre);
                return (
                  <button
                    key={ev.id}
                    type="button"
                    style={styles.agendaRow}
                    onClick={() => setAgendaSheet({ type: 'evento', id: ev.id })}
                  >
                    <span style={{ ...styles.agendaColorBar, background: color }} />
                    <span style={styles.agendaRowBody}>
                      <span style={styles.agendaRowTitle}>{code}: {ev.titulo}</span>
                      <span style={styles.agendaRowMeta}>{proj?.nombre || 'Proyecto eliminado'}</span>
                    </span>
                    {(ev.responsables || []).length > 0 && (
                      <span style={styles.agendaBadges}>
                        {(ev.responsables || []).map(r => (
                          <span key={r} style={{ ...styles.respBadge, background: memberColor(r) }} title={r}>
                            {initialsOf(r)}
                          </span>
                        ))}
                      </span>
                    )}
                  </button>
                );
              })}
              {dayAusencias.map(a => (
                <button
                  key={a.id}
                  type="button"
                  style={{ ...styles.agendaRow, background: AUSENCIA_BG }}
                  onClick={() => setAgendaSheet({ type: 'ausencia', id: a.id })}
                >
                  <span style={styles.agendaRowBody}>
                    <span style={styles.agendaRowTitle}>{a.etiqueta || 'AUSENCIA'}</span>
                    <span style={styles.agendaRowMeta}>
                      {a.responsable_nombre} · {fechaKey(a.fecha_inicio)} → {fechaKey(a.fecha_fin)}
                    </span>
                  </span>
                </button>
              ))}
            </section>
          );
        })}
      </div>
    );
  }

  return (
    <div style={isMobile ? { ...styles.page, ...pageMobile } : styles.page}>
      <div style={isMobile ? { ...styles.headerRow, ...styles.headerRowMobile } : styles.headerRow}>
        <div>
          <h1 style={isMobile ? { ...styles.title, ...styles.titleMobile } : styles.title}>Cronograma de Proyectos</h1>
          <p style={isMobile ? { ...styles.subtitle, ...styles.subtitleMobile } : styles.subtitle}>Eventos y ausencias del equipo por día laborable</p>
        </div>
        <div style={isMobile ? { ...styles.toolbarBtns, ...styles.toolbarBtnsMobile } : styles.toolbarBtns}>
          {isCompact && (
            <button
              style={{ ...styles.eventoBtnCompact, ...styles.toolbarBtnCompact }}
              onClick={() => openEventModal(todayKey >= rangeStart && todayKey <= rangeEnd ? todayKey : rangeStart)}
            >
              + Evento
            </button>
          )}
          {isAdmin && (
            <button
              style={isCompact ? { ...styles.feriadoBtn, ...styles.toolbarBtnCompact } : styles.feriadoBtn}
              onClick={() => setFeriadoModal({ fecha: '', nombre: '' })}
            >
              + Feriado
            </button>
          )}
          <button
            style={isCompact ? { ...styles.ausenciaBtn, ...styles.toolbarBtnCompact } : styles.ausenciaBtn}
            onClick={() => setAusenciaModal({
              id: null,
              responsable_nombre: '',
              fecha_inicio: '', fecha_fin: '', etiqueta: 'AUSENCIA',
            })}
          >
            + Ausencia
          </button>
        </div>
      </div>

      <div style={isCompact ? { ...styles.navRow, ...styles.navRowCompact } : styles.navRow}>
        <button style={isCompact ? { ...styles.navBtn, ...styles.navBtnCompact } : styles.navBtn} onClick={() => shiftMonth(-1)} aria-label="Mes anterior">
          <CalendarNavIcon dir="left" />
        </button>
        <div style={isCompact ? { ...styles.navTitle, ...styles.navTitleCompact } : styles.navTitle}>
          {MONTH_NAMES[monthA.getMonth()]} {monthA.getFullYear()}
        </div>
        <button style={isCompact ? { ...styles.navBtn, ...styles.navBtnCompact } : styles.navBtn} onClick={() => shiftMonth(1)} aria-label="Mes siguiente">
          <CalendarNavIcon dir="right" />
        </button>
      </div>

      {loading ? (
        <div style={styles.loading}>Cargando cronograma…</div>
      ) : (
        <div style={isCompact ? { ...styles.body, ...styles.bodyCompact } : styles.body}>
          <div style={isCompact ? { ...styles.monthsArea, ...styles.monthsAreaCompact } : styles.monthsArea}>
            {isCompact ? renderAgenda() : renderMonth(monthA)}
          </div>

          {isCompact && (
            <button
              type="button"
              style={styles.legendToggle}
              onClick={() => setLegendOpen(o => !o)}
              aria-expanded={legendOpen}
            >
              <span>Leyenda</span>
              <span aria-hidden="true">{legendOpen ? '▲' : '▼'}</span>
            </button>
          )}
          {(!isCompact || legendOpen) && (
          <aside style={isCompact ? { ...styles.legend, ...styles.legendCompact } : styles.legend}>
            <div style={styles.legendSection}>
              <div style={styles.legendTitle}>Proyectos</div>
              {visibleProjects.length === 0 && (
                <div style={styles.legendEmpty}>Sin eventos en este mes</div>
              )}
              {visibleProjects.map(p => (
                <div key={p.id} style={styles.legendRow}>
                  <span style={{ ...styles.legendDot, background: projectColorMap[p.id] }} />
                  <span style={styles.legendCode}>{projectCode(p.nombre)}</span>
                  <span style={isMobile ? { ...styles.legendName, ...styles.legendNameMobile } : styles.legendName}>{p.nombre}</span>
                </div>
              ))}
            </div>

            <div style={styles.legendSection}>
              <div style={styles.legendTitle}>Responsables</div>
              {team.map(m => (
                <div key={m.id} style={styles.legendRow}>
                  <span style={{ ...styles.respBadge, background: memberColor(m.nombre) }}>
                    {initialsOf(m.nombre)}
                  </span>
                  <span style={isMobile ? { ...styles.legendName, ...styles.legendNameMobile } : styles.legendName}>{m.nombre}</span>
                </div>
              ))}
            </div>

            <div style={styles.legendSection}>
              <div style={styles.legendTitle}>Feriados</div>
              {visibleFeriados.length === 0 && (
                <div style={styles.legendEmpty}>Sin feriados en este mes</div>
              )}
              {visibleFeriados.map(f => (
                <div key={f} style={styles.legendRow}>
                  <span style={{ ...styles.legendDot, background: HOLIDAY_BG }} />
                  <span style={isMobile ? { ...styles.legendName, ...styles.legendNameMobile } : styles.legendName}>{fmtFeriado(f)}</span>
                </div>
              ))}
            </div>

            <div style={styles.legendSection}>
              <div style={styles.legendTitle}>Feriados personalizados</div>
              {visibleCustomFeriados.length === 0 && (
                <div style={styles.legendEmpty}>Sin feriados personalizados</div>
              )}
              {visibleCustomFeriados.map(f => (
                <div key={f.id} style={styles.legendRow}>
                  <span style={{ ...styles.legendDot, background: HOLIDAY_BG }} />
                  <span style={isMobile ? { ...styles.legendName, ...styles.legendNameMobile } : styles.legendName}>
                    {fmtFeriado(fechaKey(f.fecha))} — {f.nombre}
                  </span>
                  {isAdmin && (
                    <button
                      style={isCompact ? { ...styles.legendDeleteBtn, ...styles.legendDeleteBtnCompact } : styles.legendDeleteBtn}
                      onClick={() => deleteFeriado(f.id)}
                      title="Eliminar feriado"
                      aria-label={`Eliminar feriado ${f.nombre}`}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </aside>
          )}
        </div>
      )}

      {popover && (() => {
        // Single fixed-position popover so it's never clipped by month cards.
        const ev = popover.type === 'evento' ? events.find(e => e.id === popover.id) : null;
        const au = popover.type === 'ausencia' ? ausencias.find(a => a.id === popover.id) : null;
        if (!ev && !au) return null;
        const proj = ev ? projectById[ev.proyecto_id] : null;
        const color = ev ? (projectColorMap[ev.proyecto_id] || '#8B8B8B') : null;
        const left = Math.max(8, Math.min(popover.x, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 220));
        return (
          <div
            style={{ ...styles.popover, left, top: popover.y }}
            onMouseEnter={cancelClosePopover}
            onMouseLeave={scheduleClosePopover}
          >
            {ev ? (
              <>
                <div style={styles.popoverTitle}>{ev.titulo}</div>
                <div style={styles.popoverRow}>
                  <span style={{ ...styles.legendDot, background: color }} />
                  {proj?.nombre || 'Proyecto eliminado'}
                </div>
                {(ev.responsables || []).length > 0 && (
                  <div style={styles.popoverRow}>{(ev.responsables || []).join(', ')}</div>
                )}
                <div style={styles.popoverRow}>{fechaKey(ev.fecha)}</div>
                <div style={styles.popoverActions}>
                  <button style={styles.editBtn} onClick={() => openEditEventModal(ev)}>
                    <EditIcon /> Editar
                  </button>
                  <button style={styles.deleteBtn} onClick={() => setConfirmDelete({ type: 'evento', id: ev.id })}>
                    <TrashIcon /> Eliminar
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={styles.popoverTitle}>{au.etiqueta || 'AUSENCIA'}</div>
                <div style={styles.popoverRow}>{au.responsable_nombre}</div>
                <div style={styles.popoverRow}>
                  {fechaKey(au.fecha_inicio)} → {fechaKey(au.fecha_fin)}
                </div>
                <div style={styles.popoverActions}>
                  <button style={styles.editBtn} onClick={() => openEditAusenciaModal(au)}>
                    <EditIcon /> Editar
                  </button>
                  <button style={styles.deleteBtn} onClick={() => setConfirmDelete({ type: 'ausencia', id: au.id })}>
                    <TrashIcon /> Eliminar
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {isCompact && agendaSheet && (() => {
        // Tap equivalent of the hover popover on compact viewports.
        const ev = agendaSheet.type === 'evento' ? events.find(e => e.id === agendaSheet.id) : null;
        const au = agendaSheet.type === 'ausencia' ? ausencias.find(a => a.id === agendaSheet.id) : null;
        if (!ev && !au) return null;
        const proj = ev ? projectById[ev.proyecto_id] : null;
        const color = ev ? (projectColorMap[ev.proyecto_id] || '#8B8B8B') : null;
        const closeSheet = () => setAgendaSheet(null);
        const onEdit = () => {
          closeSheet();
          if (ev) openEditEventModal(ev);
          else openEditAusenciaModal(au);
        };
        const onDelete = () => {
          closeSheet();
          setConfirmDelete({ type: ev ? 'evento' : 'ausencia', id: ev ? ev.id : au.id });
        };
        return (
          <BottomSheet
            open
            onClose={closeSheet}
            title={ev ? ev.titulo : (au.etiqueta || 'AUSENCIA')}
            footer={(
              <>
                <button type="button" style={{ ...styles.editBtn, ...styles.sheetActionBtn }} onClick={onEdit}>
                  <EditIcon /> Editar
                </button>
                <button type="button" style={{ ...styles.deleteBtn, ...styles.sheetActionBtn }} onClick={onDelete}>
                  <TrashIcon /> Eliminar
                </button>
              </>
            )}
          >
            {ev ? (
              <>
                <div style={styles.sheetRow}>
                  <span style={{ ...styles.legendDot, background: color }} />
                  {proj?.nombre || 'Proyecto eliminado'}
                </div>
                {(ev.responsables || []).length > 0 && (
                  <div style={styles.sheetRow}>{(ev.responsables || []).join(', ')}</div>
                )}
                <div style={styles.sheetRow}>{fechaKey(ev.fecha)}</div>
              </>
            ) : (
              <>
                <div style={styles.sheetRow}>{au.responsable_nombre}</div>
                <div style={styles.sheetRow}>
                  {fechaKey(au.fecha_inicio)} → {fechaKey(au.fecha_fin)}
                </div>
              </>
            )}
          </BottomSheet>
        );
      })()}

      {eventModal && (
        <div style={isMobile ? { ...styles.overlay, ...sheet.overlay } : styles.overlay} onClick={() => setEventModal(null)}>
          <div style={isMobile ? { ...styles.modal, ...sheet.panel, ...styles.modalMobile } : styles.modal} onClick={e => e.stopPropagation()}>
            {isMobile ? (
              <div style={{ ...styles.modalHeaderMobile, ...sheet.header }}>
                <h2 style={styles.modalTitleMobile}>{eventModal.id ? 'Editar evento' : 'Nuevo evento'}</h2>
                <button type="button" style={{ ...sheet.closeBtn, ...styles.modalCloseMobile }} onClick={() => setEventModal(null)} aria-label="Cerrar">✕</button>
              </div>
            ) : (
            <h2 style={styles.modalTitle}>{eventModal.id ? 'Editar evento' : 'Nuevo evento'}</h2>
            )}

            <label style={styles.fieldLabel}>Fecha</label>
            <input
              type="date"
              style={styles.input}
              value={eventModal.fecha}
              onChange={e => setEventModal(m => ({ ...m, fecha: e.target.value }))}
            />

            <label style={styles.fieldLabel}>Proyecto</label>
            <select
              style={styles.input}
              value={eventModal.proyecto_id}
              onChange={e => setEventModal(m => ({ ...m, proyecto_id: e.target.value }))}
            >
              <option value="">— Seleccionar proyecto —</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>

            <label style={styles.fieldLabel}>Responsables</label>
            <div style={styles.chipsRow}>
              {team.map(m => {
                const selected = eventModal.responsables.includes(m.nombre);
                return (
                  <button
                    key={m.id}
                    type="button"
                    style={{
                      ...styles.chip,
                      ...(selected ? { background: memberColor(m.nombre), color: '#fff', borderColor: 'transparent' } : {}),
                      ...(isCompact ? styles.chipCompact : {}),
                    }}
                    onClick={() => setEventModal(mm => ({
                      ...mm,
                      responsables: selected
                        ? mm.responsables.filter(r => r !== m.nombre)
                        : [...mm.responsables, m.nombre],
                    }))}
                  >
                    {m.nombre}
                  </button>
                );
              })}
            </div>

            <label style={styles.fieldLabel}>Título</label>
            <input
              type="text"
              style={styles.input}
              list="cronograma-titulo-suggestions"
              placeholder="REUNIÓN 01, VISITA TÉCNICA…"
              value={eventModal.titulo}
              onChange={e => setEventModal(m => ({ ...m, titulo: e.target.value }))}
            />
            <datalist id="cronograma-titulo-suggestions">
              {TITULO_SUGGESTIONS.map(s => <option key={s} value={s} />)}
            </datalist>

            <div style={isMobile ? { ...styles.modalActions, ...styles.modalActionsMobile, ...sheet.footer } : styles.modalActions}>
              <button style={isMobile ? { ...styles.cancelBtn, ...styles.modalBtnMobile } : styles.cancelBtn} onClick={() => setEventModal(null)}>Cancelar</button>
              <button style={isMobile ? { ...styles.saveBtn, ...styles.modalBtnMobile } : styles.saveBtn} onClick={saveEvent} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {feriadoModal && isAdmin && (
        <div style={isMobile ? { ...styles.overlay, ...sheet.overlay } : styles.overlay} onClick={() => setFeriadoModal(null)}>
          <div style={isMobile ? { ...styles.modal, ...sheet.panel, ...styles.modalMobile } : styles.modal} onClick={e => e.stopPropagation()}>
            {isMobile ? (
              <div style={{ ...styles.modalHeaderMobile, ...sheet.header }}>
                <h2 style={styles.modalTitleMobile}>Nuevo feriado</h2>
                <button type="button" style={{ ...sheet.closeBtn, ...styles.modalCloseMobile }} onClick={() => setFeriadoModal(null)} aria-label="Cerrar">✕</button>
              </div>
            ) : (
            <h2 style={styles.modalTitle}>Nuevo feriado</h2>
            )}

            <label style={styles.fieldLabel}>Fecha</label>
            <input
              type="date"
              style={styles.input}
              value={feriadoModal.fecha}
              onChange={e => setFeriadoModal(m => ({ ...m, fecha: e.target.value }))}
            />

            <label style={styles.fieldLabel}>Nombre</label>
            <input
              type="text"
              style={styles.input}
              placeholder="Feriado municipal, Día del estudio…"
              value={feriadoModal.nombre}
              onChange={e => setFeriadoModal(m => ({ ...m, nombre: e.target.value }))}
            />

            <div style={isMobile ? { ...styles.modalActions, ...styles.modalActionsMobile, ...sheet.footer } : styles.modalActions}>
              <button style={isMobile ? { ...styles.cancelBtn, ...styles.modalBtnMobile } : styles.cancelBtn} onClick={() => setFeriadoModal(null)}>Cancelar</button>
              <button style={isMobile ? { ...styles.saveBtn, ...styles.modalBtnMobile } : styles.saveBtn} onClick={saveFeriado} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {ausenciaModal && (
        <div style={isMobile ? { ...styles.overlay, ...sheet.overlay } : styles.overlay} onClick={() => setAusenciaModal(null)}>
          <div style={isMobile ? { ...styles.modal, ...sheet.panel, ...styles.modalMobile } : styles.modal} onClick={e => e.stopPropagation()}>
            {isMobile ? (
              <div style={{ ...styles.modalHeaderMobile, ...sheet.header }}>
                <h2 style={styles.modalTitleMobile}>{ausenciaModal.id ? 'Editar ausencia' : 'Nueva ausencia'}</h2>
                <button type="button" style={{ ...sheet.closeBtn, ...styles.modalCloseMobile }} onClick={() => setAusenciaModal(null)} aria-label="Cerrar">✕</button>
              </div>
            ) : (
            <h2 style={styles.modalTitle}>{ausenciaModal.id ? 'Editar ausencia' : 'Nueva ausencia'}</h2>
            )}

            <label style={styles.fieldLabel}>Responsable</label>
            <select
              style={styles.input}
              value={ausenciaModal.responsable_nombre}
              onChange={e => setAusenciaModal(m => ({ ...m, responsable_nombre: e.target.value }))}
            >
              <option value="">— Seleccionar responsable —</option>
              {team.map(m => (
                <option key={m.id} value={m.nombre}>{m.nombre}</option>
              ))}
            </select>

            <label style={styles.fieldLabel}>Fecha inicio</label>
            <input
              type="date"
              style={styles.input}
              value={ausenciaModal.fecha_inicio}
              onChange={e => setAusenciaModal(m => ({ ...m, fecha_inicio: e.target.value }))}
            />

            <label style={styles.fieldLabel}>Fecha fin</label>
            <input
              type="date"
              style={styles.input}
              value={ausenciaModal.fecha_fin}
              onChange={e => setAusenciaModal(m => ({ ...m, fecha_fin: e.target.value }))}
            />

            <label style={styles.fieldLabel}>Etiqueta</label>
            <input
              type="text"
              style={styles.input}
              placeholder="AUSENCIA, VIAJE XIME…"
              value={ausenciaModal.etiqueta}
              onChange={e => setAusenciaModal(m => ({ ...m, etiqueta: e.target.value }))}
            />

            <div style={isMobile ? { ...styles.modalActions, ...styles.modalActionsMobile, ...sheet.footer } : styles.modalActions}>
              <button style={isMobile ? { ...styles.cancelBtn, ...styles.modalBtnMobile } : styles.cancelBtn} onClick={() => setAusenciaModal(null)}>Cancelar</button>
              <button style={isMobile ? { ...styles.saveBtn, ...styles.modalBtnMobile } : styles.saveBtn} onClick={saveAusencia} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div style={isMobile ? { ...styles.overlay, ...dialog.overlay } : styles.overlay} onClick={() => setConfirmDelete(null)}>
          <div style={isMobile ? { ...styles.modal, maxWidth: 340, ...dialog.panel } : { ...styles.modal, maxWidth: 340 }} onClick={e => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>
              {confirmDelete.type === 'evento' ? '¿Eliminar este evento?' : '¿Eliminar esta ausencia?'}
            </h2>
            <p style={isMobile ? { ...styles.confirmText, fontSize: 15 } : styles.confirmText}>Esta acción no se puede deshacer.</p>
            <div style={isMobile ? { ...styles.modalActions, flexWrap: 'wrap' } : styles.modalActions}>
              <button style={isMobile ? { ...styles.cancelBtn, ...styles.modalBtnMobile } : styles.cancelBtn} onClick={() => setConfirmDelete(null)}>Cancelar</button>
              <button style={isMobile ? { ...styles.confirmDeleteBtn, ...styles.modalBtnMobile } : styles.confirmDeleteBtn} onClick={runConfirmedDelete}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    padding: '32px 36px',
    fontFamily: "'DM Sans', sans-serif",
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 20,
  },
  title: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 32,
    fontWeight: 600,
    color: 'var(--wine)',
    margin: 0,
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: 13,
    color: 'var(--text-light, #8B7D7D)',
  },
  toolbarBtns: {
    display: 'flex',
    gap: 10,
    flexShrink: 0,
  },
  ausenciaBtn: {
    padding: '10px 18px',
    borderRadius: 10,
    border: 'none',
    background: AUSENCIA_BG,
    color: WINE,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    flexShrink: 0,
  },
  feriadoBtn: {
    padding: '10px 18px',
    borderRadius: 10,
    border: 'none',
    background: HOLIDAY_BG,
    color: '#4A4A4A',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    flexShrink: 0,
  },
  navRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginBottom: 18,
  },
  navBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid rgba(107,45,62,0.25)',
    background: '#fff',
    color: WINE,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  navTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 22,
    fontWeight: 600,
    color: WINE,
    minWidth: 280,
    textAlign: 'center',
  },
  loading: {
    padding: 40,
    textAlign: 'center',
    color: 'var(--text-light, #8B7D7D)',
    fontSize: 14,
  },
  body: {
    display: 'flex',
    gap: 20,
    alignItems: 'flex-start',
  },
  monthsArea: {
    flex: 1,
    display: 'flex',
    gap: 20,
    alignItems: 'flex-start',
    minWidth: 0,
  },
  monthCard: {
    flex: 1,
    minWidth: 0,
    background: '#fff',
    borderRadius: 14,
    // No overflow: hidden — it would clip content; the header carries its own
    // top radius instead.
    boxShadow: '0 4px 18px rgba(0,0,0,0.06)',
    border: '1px solid rgba(0,0,0,0.05)',
  },
  monthHeader: {
    background: WINE,
    color: '#fff',
    borderRadius: '13px 13px 0 0',
    textAlign: 'center',
    padding: '10px 12px',
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 19,
    fontWeight: 600,
    letterSpacing: '0.04em',
  },
  dowRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    background: MAUVE,
  },
  dowCell: {
    padding: '8px 4px',
    textAlign: 'center',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: '#fff',
    borderRight: '1px solid rgba(255,255,255,0.25)',
  },
  weekRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    borderTop: '1px solid rgba(0,0,0,0.07)',
  },
  emptyCell: {
    minHeight: 112,
    height: 'auto',
    background: '#F6F2EE',
    borderRight: '1px solid rgba(0,0,0,0.05)',
  },
  holidayCell: {
    minHeight: 112,
    height: 'auto',
    background: HOLIDAY_BG,
    padding: 6,
    borderRight: '1px solid rgba(0,0,0,0.05)',
  },
  holidayLabel: {
    fontSize: 10,
    fontStyle: 'italic',
    color: '#7A7A7A',
    marginTop: 2,
  },
  dayCell: {
    // min-height only — the cell grows with its events so the last week's
    // rows never cut content off; grid rows stretch to the tallest cell.
    minHeight: 112,
    height: 'auto',
    padding: 8,
    borderRight: '1px solid rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    position: 'relative',
    minWidth: 0,
  },
  dayNum: {
    fontSize: 12,
    fontWeight: 700,
    color: '#3A3A3A',
    lineHeight: 1,
  },
  // Today: inset ring instead of a real border so the grid doesn't shift.
  todayRing: {
    boxShadow: `inset 0 0 0 2px ${WINE}`,
  },
  todayCell: {
    boxShadow: `inset 0 0 0 2px ${WINE}`,
    background: 'rgba(107,45,62,0.07)',
  },
  dayNumToday: {
    color: WINE,
    fontWeight: 800,
    fontSize: 13,
  },
  eventStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    flex: 1,
    minWidth: 0,
  },
  eventPillWrap: {
    position: 'relative',
    minWidth: 0,
  },
  eventPill: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    cursor: 'pointer',
    minWidth: 0,
  },
  respBadge: {
    width: 18,
    height: 18,
    borderRadius: 4,
    color: '#fff',
    fontSize: 9,
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    letterSpacing: '0.02em',
  },
  eventBar: {
    flex: 1,
    minWidth: 0,
    color: '#fff',
    fontSize: 10.5,
    fontWeight: 700,
    padding: '4px 8px',
    borderRadius: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
  },
  ausenciaBanner: {
    marginTop: 'auto',
    background: AUSENCIA_BG,
    color: WINE,
    fontSize: 10,
    fontWeight: 700,
    textAlign: 'center',
    padding: '3px 4px',
    borderRadius: 4,
    letterSpacing: '0.04em',
    cursor: 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  popover: {
    position: 'fixed', // never clipped by month cards / scroll containers
    minWidth: 190,
    maxWidth: 260,
    background: '#fff',
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
    padding: '10px 12px',
    zIndex: 60,
    cursor: 'default',
  },
  popoverTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: WINE,
    marginBottom: 6,
  },
  popoverRow: {
    fontSize: 11,
    color: '#5A5A5A',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  popoverActions: {
    display: 'flex',
    gap: 6,
    marginTop: 6,
  },
  editBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '6px 10px',
    borderRadius: 8,
    border: 'none',
    background: 'rgba(107,45,62,0.1)',
    color: WINE,
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  deleteBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '6px 10px',
    borderRadius: 8,
    border: 'none',
    background: 'rgba(184,59,59,0.1)',
    color: '#B83B3B',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  legend: {
    width: 240,
    flexShrink: 0,
    background: '#fff',
    borderRadius: 14,
    border: '1px solid rgba(0,0,0,0.05)',
    boxShadow: '0 4px 18px rgba(0,0,0,0.06)',
    padding: '18px 18px 10px',
    position: 'sticky',
    top: 20,
    maxHeight: 'calc(100vh - 40px)',
    overflowY: 'auto',
  },
  legendSection: {
    marginBottom: 18,
  },
  legendTitle: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    fontWeight: 700,
    color: WINE,
    marginBottom: 8,
  },
  legendRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
    minWidth: 0,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  legendCode: {
    fontSize: 10,
    fontWeight: 700,
    color: '#3A3A3A',
    flexShrink: 0,
  },
  legendName: {
    fontSize: 11,
    color: '#5A5A5A',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  legendDeleteBtn: {
    marginLeft: 'auto',
    width: 22,
    height: 22,
    borderRadius: 6,
    border: 'none',
    background: 'rgba(184,59,59,0.08)',
    color: '#B83B3B',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    padding: 0,
  },
  legendEmpty: {
    fontSize: 11,
    color: '#9A9A9A',
    fontStyle: 'italic',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(42,18,25,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    padding: 20,
  },
  modal: {
    background: '#fff',
    borderRadius: 16,
    padding: '26px 28px',
    width: '100%',
    maxWidth: 420,
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 30px 80px rgba(0,0,0,0.3)',
  },
  modalTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 24,
    fontWeight: 600,
    color: 'var(--wine)',
    margin: '0 0 16px',
  },
  fieldLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#6B6B6B',
    margin: '12px 0 5px',
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 9,
    border: '1px solid rgba(0,0,0,0.15)',
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: 'border-box',
    background: '#fff',
    color: '#2A2A2A',
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  confirmText: {
    margin: '0 0 4px',
    fontSize: 13,
    color: '#5A5A5A',
  },
  chip: {
    padding: '6px 12px',
    borderRadius: 999,
    border: '1px solid rgba(0,0,0,0.18)',
    background: '#fff',
    color: '#4A4A4A',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 22,
  },
  cancelBtn: {
    padding: '9px 18px',
    borderRadius: 9,
    border: '1px solid rgba(0,0,0,0.15)',
    background: '#fff',
    color: '#5A5A5A',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  confirmDeleteBtn: {
    padding: '9px 20px',
    borderRadius: 9,
    border: 'none',
    background: '#B83B3B',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  saveBtn: {
    padding: '9px 20px',
    borderRadius: 9,
    border: 'none',
    background: 'var(--wine)',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  // ── Mobile-only (< 640px) additions, merged on top of the keys above ──
  modalMobile: {
    padding: '0 16px',
    paddingBottom: 0,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'none',
  },
  modalHeaderMobile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginLeft: -16,
    marginRight: -16,
    marginBottom: 4,
    paddingLeft: 16,
    paddingRight: 6,
    paddingBottom: 8,
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    flexShrink: 0,
  },
  modalTitleMobile: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 22,
    fontWeight: 600,
    color: 'var(--wine)',
    margin: 0,
    flex: 1,
    minWidth: 0,
  },
  modalCloseMobile: {
    background: 'none',
    border: 'none',
    padding: 0,
    fontSize: 20,
    color: '#6B6B6B',
    cursor: 'pointer',
    flexShrink: 0,
  },
  modalActionsMobile: {
    marginTop: 'auto',
    marginLeft: -16,
    marginRight: -16,
    paddingTop: 12,
    paddingLeft: 16,
    paddingRight: 16,
    borderTop: '1px solid rgba(0,0,0,0.08)',
    flexShrink: 0,
  },
  modalBtnMobile: {
    minHeight: 44,
    flex: 1,
  },
  // ── Phase 2: compact (< 1024px) agenda, header, nav and legend ──
  headerRowMobile: {
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 14,
  },
  titleMobile: {
    fontSize: 26,
  },
  subtitleMobile: {
    fontSize: 15,
  },
  toolbarBtnsMobile: {
    width: '100%',
    flexShrink: 1,
    flexWrap: 'wrap',
    gap: 8,
  },
  toolbarBtnCompact: {
    minHeight: 44,
    flex: 1,
    whiteSpace: 'nowrap',
  },
  eventoBtnCompact: {
    padding: '10px 18px',
    borderRadius: 10,
    border: 'none',
    background: WINE,
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  navRowCompact: {
    justifyContent: 'space-between',
    gap: 8,
  },
  navBtnCompact: {
    width: 44,
    height: 44,
    flexShrink: 0,
  },
  navTitleCompact: {
    minWidth: 0,
    flex: 1,
  },
  bodyCompact: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
  },
  monthsAreaCompact: {
    order: 2,
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
  },
  agenda: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minWidth: 0,
  },
  agendaEmpty: {
    padding: '28px 16px',
    textAlign: 'center',
    color: '#8B7D7D',
    fontSize: 15,
    background: '#fff',
    borderRadius: 14,
    border: '1px solid rgba(0,0,0,0.05)',
  },
  agendaDay: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 0,
  },
  agendaDayHeader: {
    position: 'sticky',
    top: COMPACT_TOPBAR_OFFSET,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    background: 'var(--beige, #F7F3EE)',
    padding: '4px 0',
    borderBottom: '1px solid rgba(107,45,62,0.15)',
  },
  agendaDayLabel: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 20,
    fontWeight: 600,
    color: '#3A3A3A',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  agendaTodayTag: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#fff',
    background: WINE,
    borderRadius: 999,
    padding: '2px 8px',
  },
  agendaAddBtn: {
    width: 44,
    height: 44,
    borderRadius: 10,
    border: '1px solid rgba(107,45,62,0.25)',
    background: '#fff',
    color: WINE,
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  agendaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    minHeight: 52,
    padding: '8px 12px',
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.06)',
    background: '#fff',
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: 'border-box',
    minWidth: 0,
  },
  agendaRowStatic: {
    cursor: 'default',
  },
  agendaColorBar: {
    width: 6,
    alignSelf: 'stretch',
    borderRadius: 3,
    flexShrink: 0,
  },
  agendaRowBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  agendaRowTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: WINE,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  agendaRowMeta: {
    fontSize: 14,
    color: '#5A5A5A',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  agendaBadges: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 3,
    maxWidth: 84,
    flexShrink: 0,
  },
  legendToggle: {
    order: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: 44,
    padding: '0 16px',
    borderRadius: 10,
    border: '1px solid rgba(107,45,62,0.2)',
    background: '#fff',
    color: WINE,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: 'border-box',
  },
  legendCompact: {
    order: 1,
    width: '100%',
    position: 'static',
    maxHeight: 'none',
    overflowY: 'visible',
    boxSizing: 'border-box',
  },
  legendNameMobile: {
    fontSize: 15,
    minWidth: 0,
  },
  legendDeleteBtnCompact: {
    width: 44,
    height: 44,
  },
  chipCompact: {
    minHeight: 44,
    padding: '6px 14px',
  },
  sheetRow: {
    fontSize: 15,
    color: '#3A3A3A',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  sheetActionBtn: {
    flex: 1,
    minHeight: 44,
    justifyContent: 'center',
    fontSize: 15,
  },
};
